import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Button, InputNumber, Typography } from 'antd';
import { ColumnWidthOutlined } from '@ant-design/icons';

import SelectControl from '../../controls/SelectControl/SelectControl';
import PositioningControl from '../../controls/PositioningControl/PositioningControl';
import { refreshTransform } from '../PropertiesHelper';
import { scaleGroup } from '../../../services/ScaleService';
import {
  getAbsoluteElementPosition,
  getParentPosition,
  refreshParentGroup,
} from '../../../services/PropertiesService';
import {
  distributionAlignHorizontalDataSource,
  distributionAlignVerticalDataSource,
  distributionCenterAxisDataSource,
  distributionDirectionsDataSource,
} from './GroupAppearanceConstants';
import { ReactComponent as MinusIcon } from 'svg/graphicEditor/minus-big-black.svg';
import { ReactComponent as PlusIcon } from 'svg/graphicEditor/plus-big-black.svg';

import usePropertiesStore from '../../../stores/usePropertiesStore';
import useAttachedElementsStore from '../../../stores/useAttachedElementsStore';

import positionStyles from '../../controls/PositionSizeControl/PositionSizeControl.module.css';
import myStyles from './GroupAppearance.module.css';
import globalStyles from '../../GlobalGraphicEditor.module.css';

const { Text } = Typography;

export default function () {
  const group = usePropertiesStore((s) => s.selectedGroup);
  const refreshToggle = usePropertiesStore((s) => s.propertiesRefreshTrigger);
  const onPropertyChanged = usePropertiesStore((s) => s.propertyChanged);

  const [layoutPositioning, setLayoutPositioning] = useState();
  const [positionX, setPositionX] = useState();
  const [positionY, setPositionY] = useState();
  const [groupWidth, setGroupWidth] = useState();
  const [groupHeight, setGroupHeight] = useState();
  const [distribute, setDistribute] = useState();
  const [distributeDirection, setDistributeDirection] = useState();
  const [distributeAlign, setDistributeAlign] = useState();
  const [distributeOffset, setDistributeOffset] = useState();
  const [inputGroupWidth, setInputGroupWidth] = useState();
  const [inputGroupHeight, setInputGroupHeight] = useState();
  const [isDistributeOffsetEnabled, setDistributeOffsetEnabled] =
    useState(false);

  const minGroupWidth = 25;
  const minGroupHeight = 25;

  useEffect(() => {
    const stage = group?.getStage();
    if (!stage) return;
    const groupClientRect = group?.getClientRect();
    const canvasPosition = stage.findOne('#canvas_bg').position();
    const position = getAbsoluteElementPosition(group, canvasPosition);

    const distributeOffset = group?.getAttr('distributeOffset') ?? 0;

    setPositionX(position.x);
    setPositionY(position.y);

    setGroupWidth(parseInt(groupClientRect.width));
    setGroupHeight(parseInt(groupClientRect.height));

    setInputGroupWidth(parseInt(groupClientRect.width));
    setInputGroupHeight(parseInt(groupClientRect.height));

    setDistribute(group?.hasName('distributed'));
    const rawDirection =
      group?.getAttr('distributeDirection') ??
      distributionDirectionsDataSource[0].id;
    setDistributeDirection(rawDirection);
    let rawAlign =
      group?.getAttr('distributeAlign') ??
      distributionAlignVerticalDataSource[0].id;
    if (rawDirection === 'center' && !['1', '2'].includes(String(rawAlign))) {
      rawAlign = distributionCenterAxisDataSource[0].id;
    }
    setDistributeAlign(rawAlign);
    setDistributeOffset(distributeOffset);
    setDistributeOffsetEnabled(distributeOffset !== 0);
    const attachId = group?.getAttr('attachId');
    let aid;
    if (attachId !== undefined) {
      aid = stage
        .find('.target')
        .find((el) => el.getAttr('attachIds').indexOf(attachId) >= 0);
    }
    const attachSelectId = aid ? aid._id : 'x';
    setLayoutPositioning(attachSelectId === 'x' ? 'absolute' : 'relative');
  }, [group, refreshToggle]);

  const setPropertyValue = (setter, value) => {
    setter(value);
    propertyChanged();
  };

  const changePositionX = (value) => {
    const val = parseInt(value);
    if (isNaN(val)) {
      return;
    }

    const parentPosition = getParentPosition(group);
    group.x(
      val + group.getStage().findOne('#canvas_bg').x() - parentPosition.x,
    );
    refreshParentGroup(group);
    setPropertyValue(setPositionX, val);
  };

  const changePositionY = (value) => {
    const val = parseInt(value);
    if (isNaN(val)) {
      return;
    }
    const parentPosition = getParentPosition(group);
    group.y(
      val + group?.getStage()?.findOne('#canvas_bg').y() - parentPosition.y,
    );
    refreshParentGroup(group);
    setPropertyValue(setPositionY, val);
  };

  const changeDistribute = (val) => {
    if (val) {
      useAttachedElementsStore.getState().distributeGroup(
        group,
        distributeDirection,
        distributeAlign,
        distributeOffset,
      );
    } else {
      useAttachedElementsStore.getState().destroyDistributedGroup(group);
    }
    setPropertyValue(setDistribute, val);
  };

  const changeDistributeDirection = (val) => {
    let nextAlign = distributeAlign;
    if (val === 'center' && !['1', '2'].includes(String(nextAlign))) {
      nextAlign = distributionCenterAxisDataSource[0].id;
      setDistributeAlign(nextAlign);
    }
    if (distribute) {
      useAttachedElementsStore.getState().distributeGroup(
        group,
        val,
        nextAlign,
        distributeOffset,
      );
    } else {
      useAttachedElementsStore.getState().destroyDistributedGroup(group);
    }
    setPropertyValue(setDistributeDirection, val);
  };

  const changeDistributeAlign = (val) => {
    if (distribute) {
      useAttachedElementsStore.getState().distributeGroup(
        group,
        distributeDirection,
        val,
        distributeOffset,
      );
    } else {
      useAttachedElementsStore.getState().destroyDistributedGroup(group);
    }
    setPropertyValue(setDistributeAlign, val);
  };

  const changeDistributeOffset = (value) => {
    const val = parseInt(value);
    if (isNaN(val)) {
      return;
    }

    if (distribute) {
      useAttachedElementsStore.getState().distributeGroup(
        group,
        distributeDirection,
        distributeAlign,
        val,
      );
    } else {
      useAttachedElementsStore.getState().destroyDistributedGroup(group);
    }
    setPropertyValue(setDistributeOffset, val);
  };

  const changeCancel = useRef();
  const debouncedWidthFn = _.debounce((value) => {
    if (changeCancel.current) {
      return;
    }
    changeWidthFunc(value);
  }, 1000);

  const debouncedHeightFn = _.debounce((value) => {
    if (changeCancel.current) {
      return;
    }
    changeHeightFunc(value);
  }, 1000);

  const changeWidth = (value) => {
    changeCancel.current = true;
    const val = parseInt(value);
    setInputGroupWidth(val ?? 0);

    if (isNaN(val)) {
      return;
    }

    if (val === 0 || val < minGroupWidth) {
      return;
    }

    setGroupWidth(parseInt(val));

    const groupClientRect = group.getClientRect();

    if (groupClientRect.width / val < 5) {
      changeWidthFunc(val);
    } else {
      changeCancel.current = false;
      debouncedWidthFn(value);
    }
  };

  const changeHeight = (value) => {
    changeCancel.current = true;
    const val = parseInt(value);
    setInputGroupHeight(val ?? 0);

    if (isNaN(val)) {
      return;
    }

    if (val === 0 || val < minGroupHeight) {
      return;
    }

    setGroupHeight(parseInt(val));

    const groupClientRect = group.getClientRect();
    if (groupClientRect.height / val < 5) {
      changeHeightFunc(val);
    } else {
      changeCancel.current = false;
      debouncedHeightFn(value);
    }
  };

  const changeWidthFunc = (val) => {
    const groupClientRect = group.getClientRect();
    const oldWidth = groupClientRect.width ?? 1;

    scaleGroup(group, { x: val / oldWidth, y: 1 });
    refreshTransform(group);
  };

  const changeHeightFunc = (val) => {
    const groupClientRect = group.getClientRect();
    const oldHeight = groupClientRect.height ?? 1;

    scaleGroup(group, { x: 1, y: val / oldHeight });
    refreshTransform(group);
  };

  const propertyChanged = () => {
    if (onPropertyChanged) {
      onPropertyChanged();
    }
  };

  const widthStyle = {
    width: '100%',
    color: inputGroupWidth >= minGroupWidth ? 'black' : 'red',
  };

  const heightStyle = {
    width: '100%',
    color: inputGroupHeight >= minGroupHeight ? 'black' : 'red',
  };

  const changeDistributeEnable = () => {
    changeDistribute(!distribute);
    updateDistributeState(!distribute);
  };

  const changeDistributeOffsetEnable = () => {
    setDistributeOffsetEnabled(!isDistributeOffsetEnabled);
    updateDistributeOffsetState(!isDistributeOffsetEnabled);
  };

  const updateDistributeState = (isEnabled) => {
    const element = group;
    if (!isEnabled) {
      element.setAttr('_lastStateDistribute', {
        distribute,
        distributeDirection,
        distributeAlign,
      });
      if (isDistributeOffsetEnabled) {
        element.setAttr('_lastStateDistributeOffset', { distributeOffset });
      }

      useAttachedElementsStore.getState().destroyDistributedGroup(element);
    } else {
      const state = element.getAttr('_lastStateDistribute');
      if (state) {
        const offSetState = element.getAttr('_lastStateDistributeOffset');

        useAttachedElementsStore.getState().distributeGroup(
          element,
          state.distributeDirection,
          state.distributeAlign,
          isDistributeOffsetEnabled && offSetState?.distributeOffset
            ? offSetState?.distributeOffset
            : 0,
        );

        setPropertyValue(setDistribute, state.distribute);
        setPropertyValue(setDistributeDirection, state.distributeDirection);
        setPropertyValue(setDistributeAlign, state.distributeAlign);
      }
    }
  };

  const updateDistributeOffsetState = (isEnabled) => {
    const element = group;
    if (!isEnabled) {
      element.setAttr('_lastStateDistributeOffset', { distributeOffset });
      changeDistributeOffset(0);
    } else {
      const state = element.getAttr('_lastStateDistributeOffset');
      if (state) {
        changeDistributeOffset(state.distributeOffset);
      }
    }
  };

  return (
    <div>
      <div
        className={`${globalStyles.bottomBorderDivider} ${globalStyles.bottomBigPaddings}  ${globalStyles.selectBlock} ${globalStyles.bigGapBlock}`}
      >
        <div className={positionStyles.block}>
          <div className={positionStyles.dataBlock}>
            <div className={positionStyles.inputsBlock}>
              <div className={positionStyles.label}>X</div>
              <InputNumber
                className={`${positionStyles.input} ${globalStyles.input}`}
                size='small'
                disabled={layoutPositioning !== 'absolute'}
                value={positionX}
                onChange={changePositionX}
              />
            </div>
            <div className={positionStyles.inputsBlock}>
              <div className={positionStyles.label}>Y</div>
              <InputNumber
                className={`${positionStyles.input} ${globalStyles.input}`}
                size='small'
                disabled={layoutPositioning !== 'absolute'}
                value={positionY}
                onChange={changePositionY}
              />
            </div>
          </div>
          <div className={positionStyles.dataBlock}>
            <div className={positionStyles.inputsBlock}>
              <div className={positionStyles.label}>W</div>
              <InputNumber
                className={`${positionStyles.input} ${globalStyles.input}`}
                size='small'
                style={widthStyle}
                value={groupWidth}
                onChange={changeWidth}
              />
            </div>
            <div className={positionStyles.inputsBlock}>
              <div className={positionStyles.label}>H</div>
              <InputNumber
                className={`${positionStyles.input} ${globalStyles.input}`}
                size='small'
                style={heightStyle}
                value={groupHeight}
                onChange={changeHeight}
              />
            </div>
          </div>
        </div>
        <PositioningControl
          setLayoutPositioning={setLayoutPositioning}
        />
      </div>

      <div className={globalStyles.bottomBorderDivider}>
        <div className={globalStyles.spaceBetweenBlock}>
          <Text className={globalStyles.smallTitle}>Distribution</Text>

          <Button
            shape='circle'
            type='text'
            icon={distribute ? <MinusIcon /> : <PlusIcon />}
            onClick={changeDistributeEnable}
          />
        </div>
        {distribute && (
          <div className={globalStyles.innerBlock}>
            <div className={`${globalStyles.selectBlock} ${myStyles.smallGap}`}>
              <div className={globalStyles.spaceBetweenBlock}>
                <Text className={globalStyles.text}>Direction:</Text>
                <div className={myStyles.selectWrapper}>
                  <SelectControl
                    dataSource={distributionDirectionsDataSource}
                    onChange={changeDistributeDirection}
                    value={distributeDirection}
                  />
                </div>
              </div>

              <div className={globalStyles.spaceBetweenBlock}>
                <Text className={globalStyles.text}>Align:</Text>
                <div className={myStyles.selectWrapper}>
                  <SelectControl
                    dataSource={
                      distributeDirection === 'center'
                        ? distributionCenterAxisDataSource
                        : ['left', 'right'].indexOf(distributeDirection) >= 0
                          ? distributionAlignVerticalDataSource
                          : distributionAlignHorizontalDataSource
                    }
                    onChange={changeDistributeAlign}
                    value={distributeAlign}
                  />
                </div>
              </div>
            </div>
            <div className={globalStyles.innerBlock}>
              <div className={globalStyles.spaceBetweenBlock}>
                <Text className={globalStyles.smallTitle}>Offset</Text>
                <Button
                  shape='circle'
                  type='text'
                  icon={
                    isDistributeOffsetEnabled ? <MinusIcon /> : <PlusIcon />
                  }
                  onClick={changeDistributeOffsetEnable}
                />
              </div>
              {isDistributeOffsetEnabled && (
                <div className={globalStyles.innerBlock}>
                  <div className={globalStyles.spaceBetweenBlock}>
                    <Text className={globalStyles.text}>Offset settings:</Text>
                    <InputNumber
                      prefix={<ColumnWidthOutlined />}
                      size='small'
                      value={distributeOffset}
                      onChange={changeDistributeOffset}
                      className={globalStyles.input}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
